
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import {
  Alert,
  Avatar,
  Badge,
  Breadcrumb,
  Button,
  Card,
  Col,
  Divider,
  Empty,
  Form,
  Input,
  Modal,
  Pagination,
  Popconfirm,
  Progress,
  Row,
  Select,
  Space,
  Statistic,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';

import {
  ArrowLeftOutlined,
  CheckCircleOutlined,
  DeleteOutlined,
  DownloadOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
  FilterOutlined,
  MergeCellsOutlined,
  PlusOutlined,
  ReloadOutlined,
  SearchOutlined,
  DatabaseOutlined,
  WarningOutlined,
  EyeOutlined,
  CloseOutlined,
  ToolOutlined,
} from '@ant-design/icons';

import { useNavigate } from 'react-router-dom';
import api from '../../../api/axios';

import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

const { Title, Text } = Typography;

const PAGE_SIZE = 20;

const SparepartAudit = () => {
  const navigate = useNavigate();

  const [spareparts, setSpareparts] = useState([]);

  const [duplicates, setDuplicates] = useState({
    byImporterId: [],
    byPartName: [],
    byPartNumber: [],
  });

  const [loading, setLoading] = useState(false);
  const [duplicateLoading, setDuplicateLoading] =
    useState(false);

  const [messageApi, contextHolder] =
    message.useMessage();

  const [activeTab, setActiveTab] =
    useState('duplicates');

  const [duplicatePage, setDuplicatePage] =
    useState(1);

  const [duplicateSearch, setDuplicateSearch] =
    useState('');

  const [duplicateType, setDuplicateType] =
    useState('all');

  const [duplicateSize, setDuplicateSize] =
    useState('all');

  const [expandedGroups, setExpandedGroups] =
    useState([]);

  const [selectedGroup, setSelectedGroup] =
    useState(null);

  const [compareVisible, setCompareVisible] =
    useState(false);

  const [modalVisible, setModalVisible] =
    useState(false);

  const [editingItem, setEditingItem] =
    useState(null);

  const [form] = Form.useForm();

  /*
   * =========================================================
   * FETCH SPAREPARTS
   * =========================================================
   */

  const fetchSpareparts = useCallback(async () => {
    try {
      setLoading(true);

      const res = await api.get(
        '/api/spareparts'
      );

      setSpareparts(
        Array.isArray(res.data)
          ? res.data
          : []
      );
    } catch (err) {
      console.error(
        'Failed to fetch spareparts:',
        err
      );

      messageApi.error(
        'Failed to fetch spareparts'
      );
    } finally {
      setLoading(false);
    }
  }, [messageApi]);

  /*
   * =========================================================
   * FETCH DUPLICATES
   * =========================================================
   */

  const fetchDuplicates = useCallback(
    async (showError = true) => {
      try {
        setDuplicateLoading(true);

        const res = await api.get(
          '/api/spareparts/duplicates'
        );

        const data = res.data || {};

        setDuplicates({
          byImporterId:
            Array.isArray(
              data.byImporterId
            )
              ? data.byImporterId
              : [],

          byPartName:
            Array.isArray(
              data.byPartName
            )
              ? data.byPartName
              : [],

          byPartNumber:
            Array.isArray(
              data.byPartNumber
            )
              ? data.byPartNumber
              : [],
        });
      } catch (err) {
        console.error(
          'Failed to fetch duplicates:',
          err
        );

        if (showError) {
          messageApi.error(
            'Failed to fetch duplicate audit'
          );
        }
      } finally {
        setDuplicateLoading(false);
      }
    },
    [messageApi]
  );

  /*
   * =========================================================
   * INITIAL LOAD
   * =========================================================
   */

  useEffect(() => {
    fetchSpareparts();
    fetchDuplicates(false);
  }, [
    fetchSpareparts,
    fetchDuplicates,
  ]);

  /*
   * =========================================================
   * CREATE / EDIT
   * =========================================================
   */

  const handleCreate = () => {
    setEditingItem(null);

    form.resetFields();

    form.setFieldsValue({
      measureUnit: 'pcs',
      price: 0,
      retailPrice: 0,
      isActive: true,
    });

    setModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingItem(record);

    form.setFieldsValue({
      importerId:
        record.importerId || '',
      partNumber:
        record.partNumber || '',
      partName:
        record.partName || '',
      measureUnit:
        record.measureUnit || 'pcs',
      price:
        record.price ?? 0,
      retailPrice:
        record.retailPrice ?? 0,
      isActive:
        record.isActive !== false,
    });

    setModalVisible(true);
  };

  /*
   * =========================================================
   * DELETE
   * =========================================================
   */

  const handleDelete = async (id) => {
    try {
      await api.delete(
        `/api/spareparts/${id}`
      );

      messageApi.success(
        'Sparepart deleted successfully'
      );

      await Promise.all([
        fetchSpareparts(),
        fetchDuplicates(false),
      ]);
    } catch (err) {
      console.error(
        'Failed to delete sparepart:',
        err
      );

      messageApi.error(
        'Failed to delete sparepart'
      );
    }
  };

  /*
   * =========================================================
   * SUBMIT
   * =========================================================
   */

  const handleSubmit = async (values) => {
    try {
      const payload = {
        ...values,
        price:
          values.price === ''
            ? 0
            : Number(values.price || 0),

        retailPrice:
          values.retailPrice === ''
            ? 0
            : Number(
                values.retailPrice || 0
              ),

        isActive:
          values.isActive !== false,
      };

      if (editingItem) {
        await api.put(
          `/api/spareparts/${editingItem.id}`,
          payload
        );

        messageApi.success(
          'Sparepart updated successfully'
        );
      } else {
        await api.post(
          '/api/spareparts',
          payload
        );

        messageApi.success(
          'Sparepart created successfully'
        );
      }

      setModalVisible(false);

      setEditingItem(null);

      form.resetFields();

      await Promise.all([
        fetchSpareparts(),
        fetchDuplicates(false),
      ]);
    } catch (err) {
      console.error(
        'Sparepart save error:',
        err
      );

      messageApi.error(
        editingItem
          ? 'Failed to update sparepart'
          : 'Failed to create sparepart'
      );
    }
  };

  /*
   * =========================================================
   * EXPORT
   * =========================================================
   */

  const exportToExcel = () => {
    if (!spareparts.length) {
      messageApi.info(
        'There are no spareparts to export.'
      );

      return;
    }

    const data = spareparts.map(
      (part) => ({
        ID: part.id,

        'Importer ID':
          part.importerId || '',

        'Part Number':
          part.partNumber || '',

        'Part Name':
          part.partName || '',

        'Measure Unit':
          part.measureUnit || '',

        Price:
          part.price ?? 0,

        'Retail Price':
          part.retailPrice ?? 0,

        Active:
          part.isActive
            ? 'Yes'
            : 'No',

        'Created At':
          part.createdAt
            ? new Date(
                part.createdAt
              ).toLocaleString()
            : '',

        'Updated At':
          part.updatedAt
            ? new Date(
                part.updatedAt
              ).toLocaleString()
            : '',
      })
    );

    const worksheet =
      XLSX.utils.json_to_sheet(
        data
      );

    worksheet['!cols'] = [
      { wch: 8 },
      { wch: 18 },
      { wch: 20 },
      { wch: 36 },
      { wch: 15 },
      { wch: 14 },
      { wch: 16 },
      { wch: 10 },
      { wch: 22 },
      { wch: 22 },
    ];

    const workbook =
      XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(
      workbook,
      worksheet,
      'Spareparts'
    );

    const excelBuffer =
      XLSX.write(workbook, {
        bookType: 'xlsx',
        type: 'array',
      });

    const blob = new Blob(
      [excelBuffer],
      {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      }
    );

    saveAs(
      blob,
      `Spareparts_Audit_${new Date()
        .toISOString()
        .slice(0, 10)}.xlsx`
    );
  };

  /*
   * =========================================================
   * NORMALIZATION
   * =========================================================
   */

  const normalizeValue = (value) => {
    if (
      value === null ||
      value === undefined
    ) {
      return '';
    }

    return String(value)
      .trim()
      .toLowerCase()
      .replace(/\s+/g, ' ');
  };

  /*
   * =========================================================
   * BUILD DUPLICATE GROUPS
   *
   * Existing API:
   *
   * byImporterId
   * byPartName
   * byPartNumber
   *
   * becomes:
   *
   * [
   *   {
   *      key,
   *      type,
   *      value,
   *      records: []
   *   }
   * ]
   * =========================================================
   */

  const duplicateGroups = useMemo(() => {
    const groups = new Map();

    const addGroup = (
      type,
      value,
      sourceRecords
    ) => {
      const normalized =
        normalizeValue(value);

      if (
        !normalized ||
        !Array.isArray(
          sourceRecords
        )
      ) {
        return;
      }

      const key = `${type}:${normalized}`;

      if (!groups.has(key)) {
        groups.set(key, {
          key,
          type,
          value,
          records: [],
        });
      }

      const group =
        groups.get(key);

      sourceRecords.forEach(
        (record) => {
          if (
            !record ||
            !record.id
          ) {
            return;
          }

          const exists =
            group.records.some(
              (item) =>
                item.id ===
                record.id
            );

          if (!exists) {
            group.records.push(
              record
            );
          }
        }
      );
    };

    /*
     * Importer ID
     */

    (
      duplicates.byImporterId ||
      []
    ).forEach((record) => {
      if (!record.importerId) {
        return;
      }

      const value =
        normalizeValue(
          record.importerId
        );

      const records =
        (
          duplicates.byImporterId ||
          []
        ).filter(
          (item) =>
            normalizeValue(
              item.importerId
            ) === value
        );

      addGroup(
        'importerId',
        record.importerId,
        records
      );
    });

    /*
     * Part Name
     */

    (
      duplicates.byPartName ||
      []
    ).forEach((record) => {
      if (!record.partName) {
        return;
      }

      const value =
        normalizeValue(
          record.partName
        );

      const records =
        (
          duplicates.byPartName ||
          []
        ).filter(
          (item) =>
            normalizeValue(
              item.partName
            ) === value
        );

      addGroup(
        'partName',
        record.partName,
        records
      );
    });

    /*
     * Part Number
     */

    (
      duplicates.byPartNumber ||
      []
    ).forEach((record) => {
      if (!record.partNumber) {
        return;
      }

      const value =
        normalizeValue(
          record.partNumber
        );

      const records =
        (
          duplicates.byPartNumber ||
          []
        ).filter(
          (item) =>
            normalizeValue(
              item.partNumber
            ) === value
        );

      addGroup(
        'partNumber',
        record.partNumber,
        records
      );
    });

    return Array.from(
      groups.values()
    )
      .filter(
        (group) =>
          group.records.length > 1
      )
      .sort((a, b) => {
        if (
          b.records.length !==
          a.records.length
        ) {
          return (
            b.records.length -
            a.records.length
          );
        }

        return String(
          a.value
        ).localeCompare(
          String(b.value)
        );
      });
  }, [duplicates]);

  /*
   * =========================================================
   * UNIQUE DUPLICATE RECORDS
   * =========================================================
   */

  const uniqueDuplicateRecordIds =
    useMemo(() => {
      const ids = new Set();

      duplicateGroups.forEach(
        (group) => {
          group.records.forEach(
            (record) => {
              ids.add(record.id);
            }
          );
        }
      );

      return ids;
    }, [duplicateGroups]);

  const totalDuplicateGroups =
    duplicateGroups.length;

  const totalAffectedRecords =
    uniqueDuplicateRecordIds.size;

  /*
   * =========================================================
   * DUPLICATE TYPE COUNTS
   * =========================================================
   */

  const importerDuplicateGroups =
    duplicateGroups.filter(
      (group) =>
        group.type ===
        'importerId'
    ).length;

  const partNameDuplicateGroups =
    duplicateGroups.filter(
      (group) =>
        group.type ===
        'partName'
    ).length;

  const partNumberDuplicateGroups =
    duplicateGroups.filter(
      (group) =>
        group.type ===
        'partNumber'
    ).length;

  /*
   * =========================================================
   * FILTERING
   * =========================================================
   */

  const filteredDuplicateGroups =
    useMemo(() => {
      let result = [
        ...duplicateGroups,
      ];

      /*
       * Type
       */

      if (
        duplicateType !==
        'all'
      ) {
        result =
          result.filter(
            (group) =>
              group.type ===
              duplicateType
          );
      }

      /*
       * Size
       */

      if (
        duplicateSize ===
        '2'
      ) {
        result =
          result.filter(
            (group) =>
              group.records
                .length === 2
          );
      }

      if (
        duplicateSize ===
        '3+'
      ) {
        result =
          result.filter(
            (group) =>
              group.records
                .length >= 3
          );
      }

      /*
       * Search
       */

      const search =
        normalizeValue(
          duplicateSearch
        );

      if (search) {
        result =
          result.filter(
            (group) => {
              if (
                normalizeValue(
                  group.value
                ).includes(search)
              ) {
                return true;
              }

              return group.records.some(
                (record) =>
                  normalizeValue(
                    record.importerId
                  ).includes(
                    search
                  ) ||
                  normalizeValue(
                    record.partNumber
                  ).includes(
                    search
                  ) ||
                  normalizeValue(
                    record.partName
                  ).includes(
                    search
                  ) ||
                  String(
                    record.id
                  ).includes(
                    search
                  )
              );
            }
          );
      }

      return result;
    }, [
      duplicateGroups,
      duplicateType,
      duplicateSize,
      duplicateSearch,
    ]);

  /*
   * =========================================================
   * PAGINATION
   * =========================================================
   */

  const duplicateTotalPages =
    Math.ceil(
      filteredDuplicateGroups.length /
        PAGE_SIZE
    );

  const paginatedDuplicateGroups =
    useMemo(() => {
      const start =
        (duplicatePage - 1) *
        PAGE_SIZE;

      return filteredDuplicateGroups.slice(
        start,
        start + PAGE_SIZE
      );
    }, [
      filteredDuplicateGroups,
      duplicatePage,
    ]);

  useEffect(() => {
    if (
      duplicateTotalPages > 0 &&
      duplicatePage >
        duplicateTotalPages
    ) {
      setDuplicatePage(1);
    }
  }, [
    duplicatePage,
    duplicateTotalPages,
  ]);

  /*
   * =========================================================
   * COMPLETENESS SCORE
   * =========================================================
   */

  const getCompletenessScore = (
    part
  ) => {
    if (!part) return 0;

    let score = 0;

    if (
      part.importerId
    ) {
      score += 2;
    }

    if (
      part.partNumber
    ) {
      score += 4;
    }

    if (
      part.partName
    ) {
      score += 3;
    }

    if (
      part.measureUnit
    ) {
      score += 1;
    }

    if (
      part.price !== null &&
      part.price !== undefined
    ) {
      score += 2;
    }

    if (
      part.retailPrice !== null &&
      part.retailPrice !==
        undefined
    ) {
      score += 2;
    }

    if (
      part.isActive !==
      undefined
    ) {
      score += 1;
    }

    return score;
  };

  const getMostCompleteRecord =
    (records) => {
      if (!records?.length) {
        return null;
      }

      return [...records].sort(
        (a, b) => {
          const scoreDifference =
            getCompletenessScore(
              b
            ) -
            getCompletenessScore(
              a
            );

          if (
            scoreDifference !==
            0
          ) {
            return scoreDifference;
          }

          const aDate =
            new Date(
              a.updatedAt ||
                a.createdAt ||
                0
            ).getTime();

          const bDate =
            new Date(
              b.updatedAt ||
                b.createdAt ||
                0
            ).getTime();

          return (
            bDate - aDate
          );
        }
      )[0];
    };

  /*
   * =========================================================
   * OPEN COMPARE
   * =========================================================
   */

  const openCompare = (
    group
  ) => {
    setSelectedGroup(
      group
    );

    setCompareVisible(
      true
    );
  };

  /*
   * =========================================================
   * KEEP + DELETE OTHERS
   * =========================================================
   */

  const handleKeepAndDeleteOthers =
    (
      group,
      keeper
    ) => {
      const duplicatesToDelete =
        group.records.filter(
          (record) =>
            record.id !==
            keeper.id
        );

      if (
        !duplicatesToDelete.length
      ) {
        messageApi.info(
          'There are no duplicate records to remove.'
        );

        return;
      }

      Modal.confirm({
        title:
          'Keep this sparepart record?',
        icon: (
          <ExclamationCircleOutlined />
        ),
        width: 560,

        content: (
          <div>
            <p>
              You are selecting this
              sparepart as the primary
              record:
            </p>

            <Card
              size="small"
              style={{
                marginBottom: 12,
              }}
            >
              <Space align="start">
                <Avatar
                  icon={
                    <ToolOutlined />
                  }
                />

                <div>
                  <Text strong>
                    {keeper.partName ||
                      'Unnamed Sparepart'}
                  </Text>

                  <br />

                  <Text type="secondary">
                    ID: {keeper.id}
                  </Text>

                  <br />

                  <Text type="secondary">
                    Part Number:{' '}
                    {keeper.partNumber ||
                      'Not available'}
                  </Text>

                  <br />

                  <Text type="secondary">
                    Importer ID:{' '}
                    {keeper.importerId ||
                      'Not available'}
                  </Text>
                </div>
              </Space>
            </Card>

            <Alert
              type="warning"
              showIcon
              message={`This will permanently delete ${duplicatesToDelete.length} duplicate record(s).`}
              description="Only proceed when you are certain the other sparepart records are duplicates."
            />
          </div>
        ),

        okText: `Keep & Delete ${duplicatesToDelete.length}`,

        okType: 'danger',

        cancelText: 'Cancel',

        onOk: async () => {
          try {
            for (
              const record of duplicatesToDelete
            ) {
              await api.delete(
                `/api/spareparts/${record.id}`
              );
            }

            messageApi.success(
              `Kept sparepart #${keeper.id} and removed ${duplicatesToDelete.length} duplicate record(s).`
            );

            setCompareVisible(
              false
            );

            setSelectedGroup(
              null
            );

            await Promise.all([
              fetchSpareparts(),
              fetchDuplicates(
                false
              ),
            ]);
          } catch (err) {
            console.error(
              'Failed to remove duplicates:',
              err
            );

            messageApi.error(
              'Some duplicate records could not be removed.'
            );
          }
        },
      });
    };

  /*
   * =========================================================
   * TYPE HELPERS
   * =========================================================
   */

  const getTypeColor = (
    type
  ) => {
    switch (type) {
      case 'importerId':
        return 'red';

      case 'partName':
        return 'orange';

      case 'partNumber':
        return 'gold';

      default:
        return 'blue';
    }
  };

  const getTypeLabel = (
    type
  ) => {
    switch (type) {
      case 'importerId':
        return 'Importer ID';

      case 'partName':
        return 'Part Name';

      case 'partNumber':
        return 'Part Number';

      default:
        return 'Duplicate';
    }
  };

  /*
   * =========================================================
   * MONEY FORMAT
   * =========================================================
   */

  const formatMoney = (
    value
  ) => {
    const number =
      Number(value);

    if (
      Number.isNaN(number)
    ) {
      return '$0.00';
    }

    return `$${number.toLocaleString(
      undefined,
      {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }
    )}`;
  };

  /*
   * =========================================================
   * RECORD FIELD
   * =========================================================
   */

  const RecordField = ({
    label,
    value,
    important = false,
  }) => (
    <div
      style={{
        padding:
          '7px 0',
        borderBottom:
          '1px solid #f0f0f0',
      }}
    >
      <Text
        type="secondary"
        style={{
          display: 'block',
          fontSize: 12,
          marginBottom: 3,
        }}
      >
        {label}
      </Text>

      {value !== null &&
      value !== undefined &&
      value !== '' ? (
        <Text
          strong={important}
        >
          {value}
        </Text>
      ) : (
        <Text type="secondary">
          Not provided
        </Text>
      )}
    </div>
  );

  /*
   * =========================================================
   * SPAREPART RECORD CARD
   * =========================================================
   */

  const SparepartRecordCard = ({
    record,
    recommended,
    onEdit,
    onDelete,
    onKeep,
  }) => {
    const score =
      getCompletenessScore(
        record
      );

    const maxScore = 15;

    const percentage =
      Math.min(
        Math.round(
          (score /
            maxScore) *
            100
        ),
        100
      );

    return (
      <Card
        size="small"
        style={{
          height: '100%',
          borderRadius: 12,

          border: recommended
            ? '1px solid #52c41a'
            : '1px solid #f0f0f0',

          background:
            recommended
              ? '#f6ffed'
              : '#fff',
        }}
        bodyStyle={{
          padding: 16,
        }}
      >
        {/* Header */}

        <div
          style={{
            display: 'flex',
            justifyContent:
              'space-between',
            alignItems:
              'flex-start',
            gap: 10,
          }}
        >
          <Space align="start">
            <Avatar
              size={42}
              icon={
                <ToolOutlined />
              }
              style={{
                background:
                  recommended
                    ? '#f6ffed'
                    : '#e6f4ff',

                color:
                  recommended
                    ? '#389e0d'
                    : '#1677ff',
              }}
            />

            <div>
              <Text strong>
                {record.partName ||
                  'Unnamed Sparepart'}
              </Text>

              <div>
                <Text
                  type="secondary"
                  style={{
                    fontSize: 12,
                  }}
                >
                  Record #{record.id}
                </Text>
              </div>
            </div>
          </Space>

          {recommended && (
            <Tag
              color="success"
              icon={
                <CheckCircleOutlined />
              }
            >
              Recommended
            </Tag>
          )}
        </div>

        <Divider
          style={{
            margin:
              '12px 0',
          }}
        />

        {/* Core information */}

        <RecordField
          label="Part Name"
          value={
            record.partName
          }
          important
        />

        <RecordField
          label="Part Number"
          value={
            record.partNumber
          }
          important
        />

        <RecordField
          label="Importer ID"
          value={
            record.importerId
          }
        />

        <RecordField
          label="Measure Unit"
          value={
            record.measureUnit
          }
        />

        <RecordField
          label="Purchase Price"
          value={formatMoney(
            record.price
          )}
        />

        <RecordField
          label="Retail Price"
          value={formatMoney(
            record.retailPrice
          )}
        />

        <RecordField
          label="Status"
          value={
            record.isActive
              ? 'Active'
              : 'Inactive'
          }
        />

        {/* Completeness */}

        <div
          style={{
            marginTop: 14,
            marginBottom: 14,
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent:
                'space-between',
              marginBottom: 5,
            }}
          >
            <Text
              type="secondary"
              style={{
                fontSize: 12,
              }}
            >
              Record completeness
            </Text>

            <Text
              type="secondary"
              style={{
                fontSize: 12,
              }}
            >
              {percentage}%
            </Text>
          </div>

          <Progress
            percent={
              percentage
            }
            size="small"
            showInfo={false}
            status={
              recommended
                ? 'success'
                : 'normal'
            }
          />
        </div>

        {/* Actions */}

        <Space
          wrap
          style={{
            width: '100%',
          }}
        >
          <Button
            size="small"
            icon={
              <EditOutlined />
            }
            onClick={() =>
              onEdit(record)
            }
          >
            Edit
          </Button>

          {!recommended && (
            <Button
              size="small"
              type="primary"
              ghost
              icon={
                <CheckCircleOutlined />
              }
              onClick={() =>
                onKeep(record)
              }
            >
              Keep
            </Button>
          )}

          <Popconfirm
            title="Delete this sparepart?"
            description="This action cannot be undone."
            okText="Delete"
            cancelText="Cancel"
            okButtonProps={{
              danger: true,
            }}
            onConfirm={() =>
              onDelete(
                record.id
              )
            }
          >
            <Button
              size="small"
              danger
              icon={
                <DeleteOutlined />
              }
            >
              Delete
            </Button>
          </Popconfirm>
        </Space>
      </Card>
    );
  };

  /*
   * =========================================================
   * DUPLICATE GROUP CARD
   * =========================================================
   */

  const DuplicateGroupCard = ({
    group,
  }) => {
    const recommended =
      getMostCompleteRecord(
        group.records
      );

    const isExpanded =
      expandedGroups.includes(
        group.key
      );

    const toggleGroup = () => {
      setExpandedGroups(
        (current) =>
          current.includes(
            group.key
          )
            ? current.filter(
                (key) =>
                  key !==
                  group.key
              )
            : [
                ...current,
                group.key,
              ]
      );
    };

    return (
      <Card
        style={{
          marginBottom: 12,
          borderRadius: 12,
          overflow: 'hidden',
        }}
        bodyStyle={{
          padding: 0,
        }}
      >
        {/* Group header */}

        <div
          style={{
            padding:
              '16px 20px',

            cursor:
              'pointer',

            background:
              isExpanded
                ? '#fafafa'
                : '#fff',
          }}
          onClick={
            toggleGroup
          }
        >
          <Row
            gutter={[
              16,
              12,
            ]}
            align="middle"
          >
            {/* Main */}

            <Col
              xs={24}
              md={10}
            >
              <Space align="start">
                <Avatar
                  size={44}
                  icon={
                    <ToolOutlined />
                  }
                />

                <div>
                  <Space wrap>
                    <Text
                      strong
                      style={{
                        fontSize: 16,
                      }}
                    >
                      {group.value ||
                        'Unknown'}
                    </Text>

                    <Tag
                      color={getTypeColor(
                        group.type
                      )}
                    >
                      Duplicate by{' '}
                      {getTypeLabel(
                        group.type
                      )}
                    </Tag>
                  </Space>

                  <div
                    style={{
                      marginTop: 4,
                    }}
                  >
                    <Text
                      type="secondary"
                      style={{
                        fontSize: 13,
                      }}
                    >
                      {
                        group.records
                          .length
                      }{' '}
                      duplicate
                      records
                    </Text>
                  </div>
                </div>
              </Space>
            </Col>

            {/* Recommended */}

            <Col
              xs={12}
              md={5}
            >
              <Text
                type="secondary"
                style={{
                  fontSize: 12,
                }}
              >
                Recommended record
              </Text>

              <div>
                <Text strong>
                  #{recommended?.id}
                </Text>

                <Tag
                  color="success"
                  style={{
                    marginLeft: 8,
                  }}
                >
                  Most Complete
                </Tag>
              </div>
            </Col>

            {/* Number */}

            <Col
              xs={12}
              md={5}
            >
              <Text
                type="secondary"
                style={{
                  fontSize: 12,
                }}
              >
                Records affected
              </Text>

              <div>
                <Text strong>
                  {
                    group.records
                      .length
                  }
                </Text>
              </div>
            </Col>

            {/* Button */}

            <Col
              xs={24}
              md={4}
              style={{
                textAlign:
                  'right',
              }}
            >
              <Button
                size="small"
                icon={
                  isExpanded ? (
                    <CloseOutlined />
                  ) : (
                    <EyeOutlined />
                  )
                }
                onClick={(event) => {
                  event.stopPropagation();
                  toggleGroup();
                }}
              >
                {isExpanded
                  ? 'Close'
                  : 'Review'}
              </Button>
            </Col>
          </Row>
        </div>

        {/* Expanded records */}

        {isExpanded && (
          <div
            style={{
              padding: 20,
              borderTop:
                '1px solid #f0f0f0',
              background:
                '#fafafa',
            }}
          >
            <Row
              gutter={[
                16,
                16,
              ]}
            >
              {group.records.map(
                (record) => (
                  <Col
                    key={
                      record.id
                    }
                    xs={24}
                    lg={
                      group.records
                        .length ===
                      2
                        ? 12
                        : 8
                    }
                  >
                    <SparepartRecordCard
                      record={
                        record
                      }
                      recommended={
                        recommended?.id ===
                        record.id
                      }
                      onEdit={
                        handleEdit
                      }
                      onDelete={
                        handleDelete
                      }
                      onKeep={(
                        keeper
                      ) =>
                        handleKeepAndDeleteOthers(
                          group,
                          keeper
                        )
                      }
                    />
                  </Col>
                )
              )}
            </Row>

            <div
              style={{
                marginTop: 16,
              }}
            >
              <Alert
                type="info"
                showIcon
                message="Review before removing duplicate spareparts"
                description="The recommended record is automatically selected based on the available sparepart information. You can choose a different record to keep."
              />
            </div>

            <div
              style={{
                marginTop: 16,
                display:
                  'flex',
                justifyContent:
                  'flex-end',
              }}
            >
              <Button
                type="primary"
                danger
                icon={
                  <MergeCellsOutlined />
                }
                onClick={() =>
                  openCompare(
                    group
                  )
                }
              >
                Review & Resolve
              </Button>
            </div>
          </div>
        )}
      </Card>
    );
  };

  /*
   * =========================================================
   * NORMAL SPAREPART TABLE
   * =========================================================
   */

  const sparepartColumns =
    [
      {
        title:
          'Sparepart',
        key: 'sparepart',
        fixed: 'left',
        width: 280,

        render: (
          _,
          record
        ) => (
          <Space>
            <Avatar
              size="small"
              icon={
                <ToolOutlined />
              }
            />

            <div>
              <Text strong>
                {record.partName ||
                  'Unnamed Sparepart'}
              </Text>

              <div>
                <Text
                  type="secondary"
                  style={{
                    fontSize: 12,
                  }}
                >
                  ID #{record.id}
                </Text>
              </div>
            </div>
          </Space>
        ),
      },

      {
        title:
          'Importer ID',
        dataIndex:
          'importerId',
        key: 'importerId',
        width: 160,

        render: (
          value
        ) =>
          value || (
            <Text type="secondary">
              —
            </Text>
          ),
      },

      {
        title:
          'Part Number',
        dataIndex:
          'partNumber',
        key: 'partNumber',
        width: 180,

        render: (
          value
        ) =>
          value ? (
            <Text strong>
              {value}
            </Text>
          ) : (
            <Text type="secondary">
              —
            </Text>
          ),
      },

      {
        title:
          'UOM',
        dataIndex:
          'measureUnit',
        key: 'measureUnit',
        width: 100,

        render: (
          value
        ) =>
          value || '—',
      },

      {
        title:
          'Purchase Price',
        dataIndex:
          'price',
        key: 'price',
        width: 150,

        render: (
          value
        ) => (
          <Text strong>
            {formatMoney(
              value
            )}
          </Text>
        ),
      },

      {
        title:
          'Retail Price',
        dataIndex:
          'retailPrice',
        key: 'retailPrice',
        width: 150,

        render: (
          value
        ) => (
          <Text strong>
            {formatMoney(
              value
            )}
          </Text>
        ),
      },

      {
        title:
          'Status',
        dataIndex:
          'isActive',
        key: 'isActive',
        width: 100,

        render: (
          isActive
        ) =>
          isActive ? (
            <Tag color="green">
              Active
            </Tag>
          ) : (
            <Tag color="red">
              Inactive
            </Tag>
          ),
      },

      {
        title:
          'Created',
        dataIndex:
          'createdAt',
        key: 'createdAt',
        width: 170,

        render: (
          date
        ) =>
          date
            ? new Date(
                date
              ).toLocaleString()
            : '—',
      },

      {
        title:
          'Actions',
        key: 'actions',
        fixed: 'right',
        width: 120,

        render: (
          _,
          record
        ) => (
          <Space>
            <Tooltip title="Edit">
              <Button
                size="small"
                icon={
                  <EditOutlined />
                }
                onClick={() =>
                  handleEdit(
                    record
                  )
                }
              />
            </Tooltip>

            <Popconfirm
              title="Delete sparepart?"
              description="This action cannot be undone."
              okText="Delete"
              cancelText="Cancel"
              okButtonProps={{
                danger: true,
              }}
              onConfirm={() =>
                handleDelete(
                  record.id
                )
              }
            >
              <Tooltip title="Delete">
                <Button
                  size="small"
                  danger
                  icon={
                    <DeleteOutlined />
                  }
                />
              </Tooltip>
            </Popconfirm>
          </Space>
        ),
      },
    ];

  /*
   * =========================================================
   * COMPARE MODAL
   * =========================================================
   */

  const CompareModal =
    () => {
      if (
        !selectedGroup
      ) {
        return null;
      }

      const recommended =
        getMostCompleteRecord(
          selectedGroup.records
        );

      return (
        <Modal
          title={
            <Space>
              <MergeCellsOutlined />

              <span>
                Review Duplicate
                Spareparts
              </span>
            </Space>
          }
          open={
            compareVisible
          }
          onCancel={() => {
            setCompareVisible(
              false
            );

            setSelectedGroup(
              null
            );
          }}
          footer={null}
          width={1100}
          centered
        >
          <Alert
            type="warning"
            showIcon
            style={{
              marginBottom: 20,
            }}
            message={`Found ${selectedGroup.records.length} possible duplicate sparepart records`}
            description={`Matched by ${getTypeLabel(
              selectedGroup.type
            )}: ${selectedGroup.value}`}
          />

          <Row
            gutter={[
              16,
              16,
            ]}
          >
            {selectedGroup.records.map(
              (record) => (
                <Col
                  xs={24}
                  md={
                    selectedGroup
                      .records
                      .length ===
                    2
                      ? 12
                      : 8
                  }
                  key={
                    record.id
                  }
                >
                  <SparepartRecordCard
                    record={
                      record
                    }
                    recommended={
                      recommended?.id ===
                      record.id
                    }
                    onEdit={
                      handleEdit
                    }
                    onDelete={
                      handleDelete
                    }
                    onKeep={(
                      keeper
                    ) =>
                      handleKeepAndDeleteOthers(
                        selectedGroup,
                        keeper
                      )
                    }
                  />
                </Col>
              )
            )}
          </Row>
        </Modal>
      );
    };

  /*
   * =========================================================
   * MAIN UI
   * =========================================================
   */

  return (
    <div
     
    >
      {contextHolder}

      <div
     
      >
       

        {/* HEADER */}

        <Card
          bordered={false}
          style={{
            borderRadius: 14,
            marginBottom: 16,
          }}
          bodyStyle={{
            padding:
              '22px 24px',
          }}
        >
          <Row
            justify="space-between"
            align="middle"
            gutter={[
              16,
              16,
            ]}
          >
            <Col>
              <Space align="start">
                <Avatar
                  size={48}
                  icon={
                    <DatabaseOutlined />
                  }
                  style={{
                    background:
                      '#e6f4ff',
                    color:
                      '#1677ff',
                  }}
                />

                <div>
                  <Title
                    level={3}
                    style={{
                      margin: 0,
                    }}
                  >
                    Sparepart Audit
                  </Title>

                  <Text type="secondary">
                    Manage spareparts,
                    identify duplicate
                    records and maintain
                    clean inventory data.
                  </Text>
                </div>
              </Space>
            </Col>

            <Col>
              <Space wrap>
                <Button
                  icon={
                    <DownloadOutlined />
                  }
                  onClick={
                    exportToExcel
                  }
                  disabled={
                    !spareparts.length
                  }
                >
                  Export
                </Button>

                <Button
                  type="primary"
                  icon={
                    <PlusOutlined />
                  }
                  onClick={
                    handleCreate
                  }
                >
                  Add Sparepart
                </Button>
              </Space>
            </Col>
          </Row>
        </Card>

        {/* STATISTICS */}

        <Row
          gutter={[
            16,
            16,
          ]}
          style={{
            marginBottom: 16,
          }}
        >
          <Col
            xs={24}
            sm={12}
            lg={6}
          >
            <Card
              bordered={false}
              style={{
                borderRadius: 12,
              }}
            >
              <Statistic
                title="Total Spareparts"
                value={
                  spareparts.length
                }
                prefix={
                  <DatabaseOutlined />
                }
              />

              <Text
                type="secondary"
                style={{
                  fontSize: 12,
                }}
              >
                Inventory master records
              </Text>
            </Card>
          </Col>

          <Col
            xs={24}
            sm={12}
            lg={6}
          >
            <Card
              bordered={false}
              style={{
                borderRadius: 12,
              }}
            >
              <Statistic
                title="Duplicate Groups"
                value={
                  totalDuplicateGroups
                }
                valueStyle={{
                  color:
                    totalDuplicateGroups >
                    0
                      ? '#cf1322'
                      : '#389e0d',
                }}
                prefix={
                  <WarningOutlined />
                }
              />

              <Text
                type="secondary"
                style={{
                  fontSize: 12,
                }}
              >
                Groups requiring review
              </Text>
            </Card>
          </Col>

          <Col
            xs={24}
            sm={12}
            lg={6}
          >
            <Card
              bordered={false}
              style={{
                borderRadius: 12,
              }}
            >
              <Statistic
                title="Records Affected"
                value={
                  totalAffectedRecords
                }
                valueStyle={{
                  color:
                    totalAffectedRecords >
                    0
                      ? '#d48806'
                      : '#389e0d',
                }}
                prefix={
                  <ExclamationCircleOutlined />
                }
              />

              <Text
                type="secondary"
                style={{
                  fontSize: 12,
                }}
              >
                Unique duplicate records
              </Text>
            </Card>
          </Col>

          <Col
            xs={24}
            sm={12}
            lg={6}
          >
            <Card
              bordered={false}
              style={{
                borderRadius: 12,
              }}
            >
              <Statistic
                title="Data Quality"
                value={
                  totalDuplicateGroups >
                  0
                    ? 'Review Needed'
                    : 'Clean'
                }
                valueStyle={{
                  color:
                    totalDuplicateGroups >
                    0
                      ? '#d48806'
                      : '#389e0d',
                  fontSize: 20,
                }}
                prefix={
                  totalDuplicateGroups >
                  0 ? (
                    <WarningOutlined />
                  ) : (
                    <CheckCircleOutlined />
                  )
                }
              />

              <Text
                type="secondary"
                style={{
                  fontSize: 12,
                }}
              >
                Current duplicate status
              </Text>
            </Card>
          </Col>
        </Row>

        {/* WORKSPACE */}

        <Card
          bordered={false}
          style={{
            borderRadius: 14,
          }}
          bodyStyle={{
            padding: 0,
          }}
        >
          {/* Tabs */}

          <div
            style={{
              borderBottom:
                '1px solid #f0f0f0',

              padding:
                '0 20px',
            }}
          >
            <Space
              size={4}
              style={{
                marginTop: 4,
              }}
            >
              <Button
                type={
                  activeTab ===
                  'duplicates'
                    ? 'primary'
                    : 'text'
                }
                danger={
                  activeTab ===
                  'duplicates'
                }
                icon={
                  <ExclamationCircleOutlined />
                }
                onClick={() =>
                  setActiveTab(
                    'duplicates'
                  )
                }
              >
                Duplicate Audit

                {totalDuplicateGroups >
                  0 && (
                  <Badge
                    count={
                      totalDuplicateGroups
                    }
                    overflowCount={
                      9999
                    }
                    style={{
                      marginLeft: 6,
                    }}
                  />
                )}
              </Button>

              <Button
                type={
                  activeTab ===
                  'spareparts'
                    ? 'primary'
                    : 'text'
                }
                icon={
                  <DatabaseOutlined />
                }
                onClick={() =>
                  setActiveTab(
                    'spareparts'
                  )
                }
              >
                All Spareparts

                <Text
                  type="secondary"
                  style={{
                    marginLeft: 6,
                  }}
                >
                  {
                    spareparts.length
                  }
                </Text>
              </Button>
            </Space>
          </div>

          {/* =================================================
              DUPLICATE AUDIT
          ================================================= */}

          {activeTab ===
            'duplicates' && (
            <div
              style={{
                padding: 20,
              }}
            >
              {totalDuplicateGroups >
                0 && (
                <Alert
                  type="warning"
                  showIcon
                  style={{
                    marginBottom: 16,
                  }}
                  message={`${totalDuplicateGroups.toLocaleString()} duplicate sparepart groups require review`}
                  description="Review each group and keep the correct sparepart master record. Do not delete records that are actually different variants or specifications."
                />
              )}

              {/* FILTER TOOLBAR */}

              <Card
                size="small"
                style={{
                  marginBottom: 16,
                  background:
                    '#fafafa',
                  borderRadius: 10,
                }}
              >
                <Row
                  gutter={[
                    12,
                    12,
                  ]}
                  align="middle"
                >
                  <Col
                    xs={24}
                    md={9}
                    lg={9}
                  >
                    <Input
                      allowClear
                      size="large"
                      prefix={
                        <SearchOutlined />
                      }
                      placeholder="Search part name, part number, importer ID or ID..."
                      value={
                        duplicateSearch
                      }
                      onChange={(
                        e
                      ) => {
                        setDuplicateSearch(
                          e
                            .target
                            .value
                        );

                        setDuplicatePage(
                          1
                        );
                      }}
                    />
                  </Col>

                  <Col
                    xs={12}
                    md={5}
                    lg={4}
                  >
                    <Select
                      size="large"
                      style={{
                        width:
                          '100%',
                      }}
                      value={
                        duplicateType
                      }
                      onChange={(
                        value
                      ) => {
                        setDuplicateType(
                          value
                        );

                        setDuplicatePage(
                          1
                        );
                      }}
                      options={[
                        {
                          value:
                            'all',
                          label:
                            'All Types',
                        },
                        {
                          value:
                            'importerId',
                          label:
                            'Duplicate Importer ID',
                        },
                        {
                          value:
                            'partNumber',
                          label:
                            'Duplicate Part Number',
                        },
                        {
                          value:
                            'partName',
                          label:
                            'Duplicate Part Name',
                        },
                      ]}
                    />
                  </Col>

                  <Col
                    xs={12}
                    md={5}
                    lg={4}
                  >
                    <Select
                      size="large"
                      style={{
                        width:
                          '100%',
                      }}
                      value={
                        duplicateSize
                      }
                      onChange={(
                        value
                      ) => {
                        setDuplicateSize(
                          value
                        );

                        setDuplicatePage(
                          1
                        );
                      }}
                      options={[
                        {
                          value:
                            'all',
                          label:
                            'Any Group Size',
                        },
                        {
                          value:
                            '2',
                          label:
                            'Exactly 2 Records',
                        },
                        {
                          value:
                            '3+',
                          label:
                            '3+ Records',
                        },
                      ]}
                    />
                  </Col>

                  <Col
                    xs={24}
                    md={5}
                    lg={7}
                    style={{
                      textAlign:
                        'right',
                    }}
                  >
                    <Space wrap>
                      <Button
                        size="large"
                        icon={
                          <ReloadOutlined />
                        }
                        loading={
                          duplicateLoading
                        }
                        onClick={() =>
                          fetchDuplicates()
                        }
                      >
                        Refresh
                      </Button>

                      <Button
                        size="large"
                        icon={
                          <FilterOutlined />
                        }
                        onClick={() => {
                          setDuplicateSearch(
                            ''
                          );

                          setDuplicateType(
                            'all'
                          );

                          setDuplicateSize(
                            'all'
                          );

                          setDuplicatePage(
                            1
                          );
                        }}
                      >
                        Reset
                      </Button>
                    </Space>
                  </Col>
                </Row>
              </Card>

              {/* TYPE SUMMARY */}

              <Row
                gutter={[
                  12,
                  12,
                ]}
                style={{
                  marginBottom: 16,
                }}
              >
                <Col
                  xs={24}
                  sm={8}
                >
                  <Card
                    size="small"
                    style={{
                      borderRadius: 10,
                    }}
                  >
                    <Space>
                      <Tag color="red">
                        IMPORTER ID
                      </Tag>

                      <div>
                        <Text strong>
                          {
                            importerDuplicateGroups
                          }
                        </Text>

                        <div>
                          <Text
                            type="secondary"
                            style={{
                              fontSize: 12,
                            }}
                          >
                            duplicate groups
                          </Text>
                        </div>
                      </div>
                    </Space>
                  </Card>
                </Col>

                <Col
                  xs={24}
                  sm={8}
                >
                  <Card
                    size="small"
                    style={{
                      borderRadius: 10,
                    }}
                  >
                    <Space>
                      <Tag color="orange">
                        PART NAME
                      </Tag>

                      <div>
                        <Text strong>
                          {
                            partNameDuplicateGroups
                          }
                        </Text>

                        <div>
                          <Text
                            type="secondary"
                            style={{
                              fontSize: 12,
                            }}
                          >
                            duplicate groups
                          </Text>
                        </div>
                      </div>
                    </Space>
                  </Card>
                </Col>

                <Col
                  xs={24}
                  sm={8}
                >
                  <Card
                    size="small"
                    style={{
                      borderRadius: 10,
                    }}
                  >
                    <Space>
                      <Tag color="gold">
                        PART NUMBER
                      </Tag>

                      <div>
                        <Text strong>
                          {
                            partNumberDuplicateGroups
                          }
                        </Text>

                        <div>
                          <Text
                            type="secondary"
                            style={{
                              fontSize: 12,
                            }}
                          >
                            duplicate groups
                          </Text>
                        </div>
                      </div>
                    </Space>
                  </Card>
                </Col>
              </Row>

              {/* RESULTS HEADER */}

              <div
                style={{
                  display:
                    'flex',
                  justifyContent:
                    'space-between',
                  alignItems:
                    'center',
                  marginBottom: 12,
                  flexWrap:
                    'wrap',
                  gap: 8,
                }}
              >
                <div>
                  <Text strong>
                    Duplicate Groups
                  </Text>

                  <Text
                    type="secondary"
                    style={{
                      marginLeft: 8,
                    }}
                  >
                    {filteredDuplicateGroups.length.toLocaleString()}{' '}
                    groups
                  </Text>
                </div>

                {filteredDuplicateGroups.length >
                  0 && (
                  <Text type="secondary">
                    Showing{' '}
                    {Math.min(
                      (duplicatePage -
                        1) *
                        PAGE_SIZE +
                        1,
                      filteredDuplicateGroups.length
                    )}
                    –
                    {Math.min(
                      duplicatePage *
                        PAGE_SIZE,
                      filteredDuplicateGroups.length
                    )}{' '}
                    of{' '}
                    {
                      filteredDuplicateGroups.length
                    }
                  </Text>
                )}
              </div>

              {/* GROUP LIST */}

              {duplicateLoading ? (
                <div
                  style={{
                    padding: 80,
                    textAlign:
                      'center',
                  }}
                >
                  <Progress
                    type="circle"
                    percent={70}
                    status="active"
                  />

                  <div
                    style={{
                      marginTop: 16,
                    }}
                  >
                    <Text type="secondary">
                      Analyzing sparepart
                      records...
                    </Text>
                  </div>
                </div>
              ) : paginatedDuplicateGroups.length >
                0 ? (
                <>
                  {paginatedDuplicateGroups.map(
                    (
                      group
                    ) => (
                      <DuplicateGroupCard
                        key={
                          group.key
                        }
                        group={
                          group
                        }
                      />
                    )
                  )}

                  <div
                    style={{
                      display:
                        'flex',
                      justifyContent:
                        'center',
                      padding:
                        '20px 0 4px',
                    }}
                  >
                    <Pagination
                      current={
                        duplicatePage
                      }
                      pageSize={
                        PAGE_SIZE
                      }
                      total={
                        filteredDuplicateGroups.length
                      }
                      showSizeChanger={
                        false
                      }
                      showQuickJumper
                      showTotal={(
                        total,
                        range
                      ) =>
                        `${range[0]}-${range[1]} of ${total} groups`
                      }
                      onChange={(
                        page
                      ) => {
                        setDuplicatePage(
                          page
                        );

                        setExpandedGroups(
                          []
                        );

                        window.scrollTo(
                          {
                            top: 0,
                            behavior:
                              'smooth',
                          }
                        );
                      }}
                    />
                  </div>
                </>
              ) : (
                <Empty
                  image={
                    Empty.PRESENTED_IMAGE_SIMPLE
                  }
                  description={
                    totalDuplicateGroups ===
                    0
                      ? 'No duplicate spareparts found'
                      : 'No duplicate groups match your filters'
                  }
                >
                  {duplicateSearch ||
                  duplicateType !==
                    'all' ||
                  duplicateSize !==
                    'all' ? (
                    <Button
                      onClick={() => {
                        setDuplicateSearch(
                          ''
                        );

                        setDuplicateType(
                          'all'
                        );

                        setDuplicateSize(
                          'all'
                        );

                        setDuplicatePage(
                          1
                        );
                      }}
                    >
                      Clear Filters
                    </Button>
                  ) : null}
                </Empty>
              )}
            </div>
          )}

          {/* =================================================
              ALL SPAREPARTS
          ================================================= */}

          {activeTab ===
            'spareparts' && (
            <div
              style={{
                padding: 20,
              }}
            >
              <div
                style={{
                  display:
                    'flex',
                  justifyContent:
                    'space-between',
                  alignItems:
                    'center',
                  marginBottom: 16,
                  flexWrap:
                    'wrap',
                  gap: 12,
                }}
              >
                <div>
                  <Title
                    level={4}
                    style={{
                      margin: 0,
                    }}
                  >
                    All Spareparts
                  </Title>

                  <Text type="secondary">
                    Browse and manage
                    sparepart master
                    records.
                  </Text>
                </div>

                <Button
                  type="primary"
                  icon={
                    <PlusOutlined />
                  }
                  onClick={
                    handleCreate
                  }
                >
                  Add Sparepart
                </Button>
              </div>

              <Table
                columns={
                  sparepartColumns
                }
                dataSource={
                  spareparts
                }
                rowKey="id"
                loading={
                  loading
                }
                pagination={{
                  pageSize: 20,
                  showSizeChanger:
                    true,
                  showQuickJumper:
                    true,
                  pageSizeOptions: [
                    10,
                    20,
                    50,
                    100,
                  ],
                  showTotal: (
                    total,
                    range
                  ) =>
                    `${range[0]}-${range[1]} of ${total} spareparts`,
                }}
                scroll={{
                  x: 1200,
                }}
                locale={{
                  emptyText: (
                    <Empty
                      description="No spareparts found"
                    />
                  ),
                }}
              />
            </div>
          )}
        </Card>
      </div>

      {/* =====================================================
          CREATE / EDIT MODAL
      ===================================================== */}

      <Modal
        title={
          <Space>
            <Avatar
              icon={
                <ToolOutlined />
              }
              style={{
                background:
                  '#e6f4ff',
                color:
                  '#1677ff',
              }}
            />

            <span>
              {editingItem
                ? 'Edit Sparepart'
                : 'Add Sparepart'}
            </span>
          </Space>
        }
        open={
          modalVisible
        }
        onCancel={() => {
          setModalVisible(
            false
          );

          setEditingItem(
            null
          );

          form.resetFields();
        }}
        onOk={() =>
          form.submit()
        }
        okText={
          editingItem
            ? 'Save Changes'
            : 'Create Sparepart'
        }
        centered
        width={720}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={
            handleSubmit
          }
        >
          <Alert
            type="info"
            showIcon
            style={{
              marginBottom: 20,
            }}
            message="Sparepart master information"
            description="Provide accurate importer ID, part number and part name. These fields are used to identify potential duplicate records."
          />

          <Row
            gutter={[
              16,
              0,
            ]}
          >
            <Col
              xs={24}
              sm={12}
            >
              <Form.Item
                name="importerId"
                label="Importer ID"
                rules={[
                  {
                    required: true,
                    message:
                      'Please enter importer ID',
                  },
                ]}
              >
                <Input
                  size="large"
                  placeholder="Enter importer ID"
                />
              </Form.Item>
            </Col>

            <Col
              xs={24}
              sm={12}
            >
              <Form.Item
                name="partNumber"
                label="Part Number"
                rules={[
                  {
                    required: true,
                    message:
                      'Please enter part number',
                  },
                ]}
              >
                <Input
                  size="large"
                  placeholder="Enter part number"
                />
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item
                name="partName"
                label="Part Name"
                rules={[
                  {
                    required: true,
                    message:
                      'Please enter part name',
                  },
                ]}
              >
                <Input
                  size="large"
                  placeholder="Enter sparepart name"
                />
              </Form.Item>
            </Col>

            <Col
              xs={24}
              sm={8}
            >
              <Form.Item
                name="measureUnit"
                label="Measure Unit"
              >
                <Input
                  size="large"
                  placeholder="e.g. pcs, set"
                />
              </Form.Item>
            </Col>

            <Col
              xs={24}
              sm={8}
            >
              <Form.Item
                name="price"
                label="Purchase Price"
              >
                <Input
                  size="large"
                  type="number"
                  min={0}
                  step="0.01"
                  placeholder="0.00"
                />
              </Form.Item>
            </Col>

            <Col
              xs={24}
              sm={8}
            >
              <Form.Item
                name="retailPrice"
                label="Retail Price"
              >
                <Input
                  size="large"
                  type="number"
                  min={0}
                  step="0.01"
                  placeholder="0.00"
                />
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item
                name="isActive"
                label="Status"
                valuePropName="checked"
              >
                <Switch
                  checkedChildren="Active"
                  unCheckedChildren="Inactive"
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      {/* =====================================================
          COMPARE / RESOLVE MODAL
      ===================================================== */}

      {selectedGroup && (
        <Modal
          title={
            <Space>
              <MergeCellsOutlined />

              <span>
                Review Duplicate
                Spareparts
              </span>
            </Space>
          }
          open={
            compareVisible
          }
          onCancel={() => {
            setCompareVisible(
              false
            );

            setSelectedGroup(
              null
            );
          }}
          footer={null}
          width={1100}
          centered
        >
          <Alert
            type="warning"
            showIcon
            style={{
              marginBottom: 20,
            }}
            message={`Found ${selectedGroup.records.length} possible duplicate sparepart records`}
            description={`Matched by ${getTypeLabel(
              selectedGroup.type
            )}: ${selectedGroup.value}`}
          />

          <Row
            gutter={[
              16,
              16,
            ]}
          >
            {selectedGroup.records.map(
              (record) => {
                const recommended =
                  getMostCompleteRecord(
                    selectedGroup.records
                  );

                return (
                  <Col
                    xs={24}
                    md={
                      selectedGroup
                        .records
                        .length ===
                      2
                        ? 12
                        : 8
                    }
                    key={
                      record.id
                    }
                  >
                    <SparepartRecordCard
                      record={
                        record
                      }
                      recommended={
                        recommended?.id ===
                        record.id
                      }
                      onEdit={
                        handleEdit
                      }
                      onDelete={
                        handleDelete
                      }
                      onKeep={(
                        keeper
                      ) =>
                        handleKeepAndDeleteOthers(
                          selectedGroup,
                          keeper
                        )
                      }
                    />
                  </Col>
                );
              }
            )}
          </Row>
        </Modal>
      )}
    </div>
  );
};

export default SparepartAudit;
